/**
 * INVI HQ — Announcements control page. System-admin only (route-gated by
 * view_insights). Create/edit/delete the announcement bars shown on the Home
 * page: Global (everyone) or per–dealer-group, with a level, an Active toggle,
 * and an optional schedule window (auto-show / auto-expire).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"

import Layout from "../../components/Layout"
import { THEME } from "../../config/theme"
import { useAuth } from "../../context/AuthContext"
import { DealerGroupOption, fetchDealerGroups } from "../../api/dealerGroups"
import {
  Announcement,
  createAnnouncement,
  deleteAnnouncement,
  fetchAnnouncements,
  updateAnnouncement,
} from "../../api/announcements"

const LEVELS = ["info", "warning", "success"] as const

interface FormState {
  scope: string
  dealer_group_id: number | null
  message: string
  level: string
  is_active: boolean
  dismissible: boolean
  starts: string // datetime-local
  ends: string // datetime-local
}

const EMPTY_FORM: FormState = {
  scope: "global",
  dealer_group_id: null,
  message: "",
  level: "info",
  is_active: true,
  dismissible: true,
  starts: "",
  ends: "",
}

const pad = (n: number) => String(n).padStart(2, "0")

function toInputValue(iso: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function toIso(value: string): string | null {
  return value ? new Date(value).toISOString() : null
}

function windowLabel(a: Announcement): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
  if (a.starts_at && a.ends_at) return `${fmt(a.starts_at)} → ${fmt(a.ends_at)}`
  if (a.ends_at) return `until ${fmt(a.ends_at)}`
  if (a.starts_at) return `from ${fmt(a.starts_at)}`
  return "Always (while active)"
}

function AnnouncementsPage() {
  const { hasPermission, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()

  const [items, setItems] = useState<Announcement[]>([])
  const [groups, setGroups] = useState<DealerGroupOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [modalError, setModalError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [rowBusyId, setRowBusyId] = useState<number | null>(null)

  useEffect(() => {
    if (authLoading) return
    if (!hasPermission("view_insights")) navigate("/dashboard", { replace: true })
  }, [authLoading, hasPermission, navigate])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [list, groupList] = await Promise.all([fetchAnnouncements(), fetchDealerGroups()])
      setItems(list)
      setGroups(groupList)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load announcements")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (authLoading || !hasPermission("view_insights")) return
    load()
  }, [authLoading, hasPermission, load])

  const groupName = useMemo(() => {
    const map: Record<number, string> = {}
    groups.forEach((g) => (map[g.id] = g.name))
    return map
  }, [groups])

  const openNew = () => {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setModalError(null)
    setModalOpen(true)
  }

  const openEdit = (a: Announcement) => {
    setEditingId(a.id)
    setForm({
      scope: a.scope,
      dealer_group_id: a.dealer_group_id,
      message: a.message,
      level: a.level,
      is_active: a.is_active,
      dismissible: a.dismissible,
      starts: toInputValue(a.starts_at),
      ends: toInputValue(a.ends_at),
    })
    setModalError(null)
    setModalOpen(true)
  }

  const save = async () => {
    setModalError(null)
    if (!form.message.trim()) {
      setModalError("Message is required")
      return
    }
    if (form.scope === "dealer_group" && form.dealer_group_id == null) {
      setModalError("Pick a dealer group")
      return
    }
    const payload = {
      scope: form.scope,
      dealer_group_id: form.scope === "dealer_group" ? form.dealer_group_id : null,
      message: form.message.trim(),
      level: form.level,
      is_active: form.is_active,
      dismissible: form.dismissible,
      starts_at: toIso(form.starts),
      ends_at: toIso(form.ends),
    }
    setSaving(true)
    try {
      if (editingId === null) await createAnnouncement(payload)
      else await updateAnnouncement(editingId, payload)
      setModalOpen(false)
      await load()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const rowAction = async (id: number, fn: () => Promise<void>) => {
    setRowBusyId(id)
    setError(null)
    try {
      await fn()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed")
    } finally {
      setRowBusyId(null)
    }
  }

  // -------------------------------------------------------------------------
  // Styles
  // -------------------------------------------------------------------------

  const inputStyle: React.CSSProperties = {
    width: "100%",
    minHeight: "40px",
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.md,
    padding: "9px 12px",
    fontSize: "14px",
    fontFamily: THEME.fonts.primary,
    color: THEME.colors.neutral.light.textPrimary,
    boxSizing: "border-box",
    marginBottom: "12px",
    outline: "none",
  }
  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: THEME.colors.neutral.light.textPrimary,
    marginBottom: "4px",
  }
  const primaryBtn: React.CSSProperties = {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    background: THEME.colors.navy,
    color: THEME.colors.white,
    fontWeight: 500,
    fontSize: "14px",
    border: "none",
    borderRadius: THEME.borderRadius.md,
    cursor: "pointer",
    fontFamily: THEME.fonts.primary,
  }
  const secondaryBtn: React.CSSProperties = {
    ...primaryBtn,
    background: THEME.colors.neutral.light.surface,
    color: THEME.colors.neutral.light.textPrimary,
    border: `1px solid ${THEME.colors.silver}`,
  }
  const linkBtn: React.CSSProperties = {
    background: "none",
    border: "none",
    color: THEME.colors.navy,
    fontWeight: 500,
    fontSize: "13px",
    cursor: "pointer",
    padding: "0 6px",
    fontFamily: THEME.fonts.primary,
  }

  if (authLoading || !hasPermission("view_insights")) {
    return (
      <Layout>
        <div />
      </Layout>
    )
  }

  return (
    <Layout>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
        <div
          style={{
            fontSize: THEME.typography.h3.size,
            fontWeight: THEME.typography.h3.weight,
            letterSpacing: THEME.typography.h3.tracking,
            color: THEME.colors.neutral.light.textPrimary,
          }}
        >
          Announcements
        </div>
        <button type="button" style={primaryBtn} onClick={openNew}>
          + New announcement
        </button>
      </div>
      <div style={{ fontSize: "13px", color: THEME.colors.neutral.light.textSecondary, marginBottom: "20px" }}>
        Control the banner at the top of the Home page — global or per dealer group, with an optional schedule.
      </div>

      {error && (
        <div
          style={{
            background: "rgba(220, 38, 38, 0.08)",
            color: THEME.colors.semantic.danger,
            border: "0.5px solid rgba(220, 38, 38, 0.3)",
            padding: "10px 14px",
            borderRadius: THEME.borderRadius.md,
            marginBottom: "16px",
            fontSize: "13px",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: THEME.colors.neutral.light.surface,
          borderRadius: THEME.borderRadius.md,
          border: `1px solid ${THEME.colors.silver}`,
          overflow: "hidden",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", fontFamily: THEME.fonts.primary }}>
          <thead>
            <tr style={{ background: THEME.auditTable.titleBarBackgroundLight }}>
              {["Audience", "Message", "Level", "Window", "Status", "Actions"].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: "10px 14px",
                    fontWeight: 500,
                    fontSize: "12px",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    color: THEME.colors.neutral.light.textSecondary,
                    borderBottom: `1px solid ${THEME.colors.silver}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={emptyCell}>Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} style={emptyCell}>No announcements yet.</td></tr>
            ) : (
              items.map((a, idx) => (
                <tr
                  key={a.id}
                  style={{ background: idx % 2 === 0 ? THEME.colors.neutral.light.surface : THEME.colors.neutral.light.background }}
                >
                  <td style={cell}>
                    {a.scope === "global" ? "Global" : groupName[a.dealer_group_id ?? -1] ?? "Dealer group"}
                  </td>
                  <td style={{ ...cell, maxWidth: "280px" }}>{a.message}</td>
                  <td style={cell}>{a.level}</td>
                  <td style={cell}>{windowLabel(a)}</td>
                  <td style={cell}>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: a.is_active ? THEME.colors.semantic.success : THEME.colors.neutral.light.textSecondary,
                      }}
                    >
                      {a.is_active ? "Active" : "Paused"}
                    </span>
                  </td>
                  <td style={cell}>
                    <button type="button" style={linkBtn} onClick={() => openEdit(a)}>Edit</button>
                    <span style={{ color: THEME.colors.silver }}>|</span>
                    <button
                      type="button"
                      style={linkBtn}
                      disabled={rowBusyId === a.id}
                      onClick={() => rowAction(a.id, () => updateAnnouncement(a.id, { is_active: !a.is_active }).then(() => {}))}
                    >
                      {a.is_active ? "Pause" : "Resume"}
                    </button>
                    <span style={{ color: THEME.colors.silver }}>|</span>
                    <button
                      type="button"
                      style={{ ...linkBtn, color: THEME.colors.semantic.danger }}
                      disabled={rowBusyId === a.id}
                      onClick={() => rowAction(a.id, () => deleteAnnouncement(a.id))}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={() => setModalOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "480px", maxWidth: "92vw", maxHeight: "90vh", overflowY: "auto",
              background: THEME.colors.neutral.light.surface, border: `1px solid ${THEME.colors.silver}`,
              borderRadius: THEME.borderRadius.lg, padding: "28px", boxSizing: "border-box", fontFamily: THEME.fonts.primary,
            }}
          >
            <div style={{ fontSize: "20px", fontWeight: 500, color: THEME.colors.neutral.light.textPrimary, marginBottom: "20px" }}>
              {editingId === null ? "New announcement" : "Edit announcement"}
            </div>

            <label style={labelStyle}>Audience</label>
            <select
              value={form.scope === "global" ? "global" : String(form.dealer_group_id ?? "")}
              onChange={(e) => {
                const v = e.target.value
                if (v === "global") setForm({ ...form, scope: "global", dealer_group_id: null })
                else setForm({ ...form, scope: "dealer_group", dealer_group_id: Number(v) })
              }}
              style={inputStyle}
            >
              <option value="global">Global — everyone</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>

            <label style={labelStyle}>Message</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              style={{ ...inputStyle, minHeight: "72px", resize: "vertical" }}
            />

            <label style={labelStyle}>Level</label>
            <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} style={inputStyle}>
              {LEVELS.map((l) => (
                <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
              ))}
            </select>

            <div style={{ display: "flex", gap: "12px" }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Show from (optional)</label>
                <input type="datetime-local" value={form.starts} onChange={(e) => setForm({ ...form, starts: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Hide after (optional)</label>
                <input type="datetime-local" value={form.ends} onChange={(e) => setForm({ ...form, ends: e.target.value })} style={inputStyle} />
              </div>
            </div>

            <label style={{ display: "flex", alignItems: "center", fontSize: "14px", color: THEME.colors.neutral.light.textPrimary, marginBottom: "10px", cursor: "pointer" }}>
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} style={{ marginRight: "8px" }} />
              Active
            </label>
            <label style={{ display: "flex", alignItems: "center", fontSize: "14px", color: THEME.colors.neutral.light.textPrimary, marginBottom: "16px", cursor: "pointer" }}>
              <input type="checkbox" checked={form.dismissible} onChange={(e) => setForm({ ...form, dismissible: e.target.checked })} style={{ marginRight: "8px" }} />
              Let users dismiss it
            </label>

            {modalError && (
              <div style={{ color: THEME.colors.semantic.danger, fontSize: "13px", marginBottom: "12px" }}>{modalError}</div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button type="button" style={secondaryBtn} onClick={() => setModalOpen(false)} disabled={saving}>Cancel</button>
              <button type="button" style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }} onClick={save} disabled={saving}>
                {saving ? "Saving…" : editingId === null ? "Create" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

const cell: React.CSSProperties = {
  padding: "10px 14px",
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
  color: THEME.colors.neutral.light.textPrimary,
  verticalAlign: "middle",
}

const emptyCell: React.CSSProperties = {
  padding: "24px",
  textAlign: "center",
  color: THEME.colors.neutral.light.textSecondary,
}

export default AnnouncementsPage
